import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Save, Check } from "lucide-react";
import { LOGO_URL } from "../context/AuthContext";
import { authApi } from "../lib/api";
import { toast } from "sonner";

export default function ResetPassword() {
    const [sp] = useSearchParams();
    const navigate = useNavigate();
    const token = sp.get("token") || "";
    const [pw, setPw] = useState("");
    const [pw2, setPw2] = useState("");
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (pw !== pw2) {
            toast.error("Passwords don't match");
            return;
        }
        setBusy(true);
        try {
            await authApi.confirmPasswordReset(token, pw);
            setDone(true);
            setTimeout(() => navigate("/login"), 1500);
        } catch (err) {
            toast.error(
                err?.response?.data?.detail || "Could not reset password",
            );
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            className="min-h-screen flex items-center justify-center px-4 py-8"
            data-testid="reset-page"
        >
            <div className="w-full max-w-md">
                <div className="bg-[#E8241A] brut-border brut-shadow p-6 sm:p-8 mb-[-20px] relative z-10">
                    <img
                        src={LOGO_URL}
                        alt="911 Cellphone Bank"
                        className="w-full h-auto"
                    />
                </div>

                <div className="bg-white brut-border brut-shadow relative">
                    <span className="brut-tape">New Password</span>
                    <div className="p-6 sm:p-8 pt-10">
                        {done ? (
                            <div
                                className="text-center space-y-3"
                                data-testid="reset-done-card"
                            >
                                <div className="inline-flex items-center gap-2 font-display text-2xl">
                                    <Check size={22} strokeWidth={3} />
                                    Password updated
                                </div>
                                <p className="text-sm">
                                    Redirecting to sign-in…
                                </p>
                            </div>
                        ) : (
                            <>
                                <h1 className="font-display text-3xl sm:text-4xl leading-none">
                                    Set new password.
                                </h1>
                                <p className="mt-3 text-sm">
                                    Must be at least 8 characters and contain
                                    one letter and one number.
                                </p>
                                <form
                                    onSubmit={handleSubmit}
                                    className="mt-6 space-y-3"
                                >
                                    <Field label="New password">
                                        <input
                                            type="password"
                                            value={pw}
                                            onChange={(e) =>
                                                setPw(e.target.value)
                                            }
                                            required
                                            className="w-full px-4 py-3 brut-border bg-white text-black placeholder:text-black/40 focus:bg-[#FAFAF7] outline-none"
                                            data-testid="reset-password-input"
                                        />
                                    </Field>
                                    <Field label="Confirm password">
                                        <input
                                            type="password"
                                            value={pw2}
                                            onChange={(e) =>
                                                setPw2(e.target.value)
                                            }
                                            required
                                            className="w-full px-4 py-3 brut-border bg-white text-black placeholder:text-black/40 focus:bg-[#FAFAF7] outline-none"
                                            data-testid="reset-password-confirm"
                                        />
                                    </Field>
                                    <button
                                        type="submit"
                                        disabled={busy || !token}
                                        className="w-full bg-[#E8241A] text-white font-display text-lg sm:text-xl px-6 py-4 brut-border brut-shadow brut-press inline-flex items-center justify-center gap-2 disabled:opacity-60"
                                        data-testid="reset-submit-btn"
                                    >
                                        {busy ? "Saving…" : "Update Password"}{" "}
                                        <Save size={20} strokeWidth={3} />
                                    </button>
                                    {!token && (
                                        <p className="text-xs text-[#E8241A] text-center">
                                            This page needs a valid reset link.
                                        </p>
                                    )}
                                </form>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="block font-display text-[11px] tracking-widest mb-1">
                {label}
            </span>
            {children}
        </label>
    );
}
